/**
 * Central seasonal configuration.
 * All campaign/banner components should import from here
 * so content stays in sync with the current season automatically.
 */

export type SeasonKey = "spring" | "summer" | "autumn" | "winter";

export interface SeasonConfig {
  key: SeasonKey;
  label: string;
  announcementDeals: { text: string; badge: string; link: string }[];
  campaignFeatured: {
    brand: string;
    headline: string;
    subtitle: string;
    discount: string;
    ctaLink: string;
  };
  campaignSide: {
    brand: string;
    headline: string;
    subtitle: string;
    discount: string;
    ctaLink: string;
  }[];
}

const seasons: Record<SeasonKey, SeasonConfig> = {
  spring: {
    key: "spring",
    label: "Vår",
    announcementDeals: [
      { text: "🐾 Välkommen till Zooi – din djurbutik online!", badge: "NY!", link: "/kampanj" },
      { text: "Fräscha upp inför våren – pälsvård & fästingskydd", badge: "VÅR", link: "/sok?q=palsvard" },
      { text: "Fri frakt upp till 15 kg · Viktfrakt från 149 kr", badge: "🚚", link: "/frakt-leverans" },
    ],
    campaignFeatured: {
      brand: "THERMOTIC",
      headline: "Värmeskydd för hund",
      subtitle: "Patenterat neoprenmaterial – utvecklat i Sverige för skandinaviska förhållanden",
      discount: "20%",
      ctaLink: "/varumarke/thermotic",
    },
    campaignSide: [
      {
        brand: "KONG & Dogman",
        headline: "Hundleksaker",
        subtitle: "Slitstarka favoriter för utelek",
        discount: "25%",
        ctaLink: "/hund/leksaker",
      },
      {
        brand: "Dogman",
        headline: "Naturligt tugg",
        subtitle: "Nötöron, vildsvin & mer",
        discount: "15%",
        ctaLink: "/hund/tugg-dental",
      },
    ],
  },
  summer: {
    key: "summer",
    label: "Sommar",
    announcementDeals: [
      { text: "☀️ Sommarfynd hos Zooi!", badge: "SOL", link: "/kampanj" },
      { text: "Upp till 50% rabatt på utvalda favoriter", badge: "NYT", link: "/kampanj" },
      { text: "Fri frakt upp till 15 kg · Viktfrakt från 149 kr", badge: "🚚", link: "/frakt-leverans" },
    ],
    campaignFeatured: {
      brand: "Sommarens måsten",
      headline: "Öppningskampanj – upp till 50%",
      subtitle: "Foder, leksaker & tillbehör till superpriser",
      discount: "50%",
      ctaLink: "/kampanj",
    },
    campaignSide: [
      {
        brand: "KONG & Dogman",
        headline: "Hundleksaker",
        subtitle: "Perfekt för aktiv lek",
        discount: "25%",
        ctaLink: "/hund/leksaker",
      },
      {
        brand: "Outlet",
        headline: "Sista chansen",
        subtitle: "Korta datum & få i lager",
        discount: "Outlet",
        ctaLink: "/outlet",
      },
    ],
  },
  autumn: {
    key: "autumn",
    label: "Höst",
    announcementDeals: [
      { text: "🍂 Höstfynd hos Zooi!", badge: "HÖST", link: "/kampanj" },
      { text: "Mysiga bäddar, täcken & varmfoder", badge: "MYS", link: "/sok?q=badd" },
      { text: "Fri frakt upp till 15 kg · Viktfrakt från 149 kr", badge: "🚚", link: "/frakt-leverans" },
    ],
    campaignFeatured: {
      brand: "Höstfavoriter",
      headline: "Mysiga bäddar & täcken",
      subtitle: "Förbered husdjuret inför kylan",
      discount: "20%",
      ctaLink: "/sok?q=badd",
    },
    campaignSide: [
      {
        brand: "KONG & Dogman",
        headline: "Hundleksaker",
        subtitle: "Inomhuslek för regniga dagar",
        discount: "25%",
        ctaLink: "/hund/leksaker",
      },
      {
        brand: "Dogman",
        headline: "Naturligt tugg",
        subtitle: "Nötöron, vildsvin & mer",
        discount: "15%",
        ctaLink: "/hund/tugg-dental",
      },
    ],
  },
  winter: {
    key: "winter",
    label: "Vinter",
    announcementDeals: [
      { text: "❄️ Vintererbjudanden hos Zooi!", badge: "VINTER", link: "/kampanj" },
      { text: "Tassvax, reflexer & varma täcken", badge: "NYT", link: "/hund/klader-uteliv" },
      { text: "Fri frakt upp till 15 kg · Viktfrakt från 149 kr", badge: "🚚", link: "/frakt-leverans" },
    ],
    campaignFeatured: {
      brand: "Vinterprodukter",
      headline: "Hundtäcken & jackor",
      subtitle: "Håll din hund varm hela vintern",
      discount: "20%",
      ctaLink: "/hund/klader-uteliv",
    },
    campaignSide: [
      {
        brand: "Reflexer & belysning",
        headline: "Synlighet i mörkret",
        subtitle: "LED-halsband, reflexvästar & mer",
        discount: "25%",
        ctaLink: "/sok?q=reflex",
      },
      {
        brand: "Dogman",
        headline: "Tassvax & skydd",
        subtitle: "Skydda tassarna mot salt & is",
        discount: "15%",
        ctaLink: "/sok?q=tassvax",
      },
    ],
  },
};

function getCurrentSeason(): SeasonKey {
  const month = new Date().getMonth(); // 0-11
  if (month >= 2 && month <= 4) return "spring";
  if (month >= 5 && month <= 7) return "summer";
  if (month >= 8 && month <= 9) return "autumn";
  return "winter";
}

/** Returns the full config for the current season */
export function getSeasonConfig(): SeasonConfig {
  return seasons[getCurrentSeason()];
}

/** Returns just the season key */
export function getCurrentSeasonKey(): SeasonKey {
  return getCurrentSeason();
}
